import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";

// Dashboard components
import Login from "./components/auth/Login";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import Dashboard from "./components/mydashboard/Dashboard";
import CompanyInfo from "./components/mydashboard/CompanyInfo";
import OffertesManager from "./components/mydashboard/OffertesManager";
import OfferteForm from "./components/mydashboard/OfferteForm";
import OfferteDetails from "./components/mydashboard/OfferteDetails";
import ItemsManager from "./components/mydashboard/ItemsManager";
import ReservationsManager from "./components/mydashboard/ReservationsManager";
import TicketsManager from "./components/mydashboard/TicketsManager";
import Statistics from "./components/mydashboard/Statistics";
import Settings from "./components/mydashboard/Settings";
import CompaniesManager from "./components/mydashboard/CompaniesManager";
import NotFound from "./components/common/NotFound";
import InboxManager from "./components/mydashboard/InboxManager";
import AdminTicketsManager from "./components/mydashboard/AdminTicketsManager";
import AdminItemsManager from "./components/mydashboard/AdminItemsManager";
import AdminFilesManager from "./components/mydashboard/AdminFilesManager";
import FilesViewer from "./components/mydashboard/FilesViewer";
import { MessageProvider } from "./context/MessageContext";
import OfferteSettingsForm from "./components/mydashboard/OfferteSettingsForm";
import ShopManager from "./components/mydashboard/ShopManager";
import OrderManager from "./components/mydashboard/OrderManager";
import EventsManager from "./components/mydashboard/EventsManager";
import EventEditor from "./components/mydashboard/EventEditor";
import EventRegistrations from "./components/mydashboard/EventRegistrations";
import PopupsManager from "./components/mydashboard/PopupsManager";
import AdminPopupsManager from "./components/mydashboard/AdminPopupsManager";
import AdminConfiguratorsManager from "./components/mydashboard/AdminConfiguratorsManager";
import ConfiguratorManager from "./components/mydashboard/ConfiguratorManager";
import ConfiguratorEditor from "./components/mydashboard/ConfiguratorEditor";
import JobApplicationsManager from "./components/mydashboard/JobApplicationsManager";
import AdminJobApplicationsManager from "./components/mydashboard/AdminJobApplicationsManager";
import AdminShopManager from "./components/mydashboard/AdminShopManager";
import PrivacyPolicy from "./components/mydashboard/PrivacyPolicy";
import TermsOfService from "./components/mydashboard/TermsOfService";
import CookieBanner from "./components/common/CookieBanner";
import AnalyticsRoutes from "./components/common/AnalyticsRoutes";
import LearningPlatformManager from "./components/mydashboard/LearningPlatformManager";
import AIVideoAnalystManager from "./components/mydashboard/AIVideoAnalyst/AIVideoAnalystManager";
import GoogleCallback from "./components/auth/GoogleCallback";
import MollieCallback from "./components/auth/MollieCallback";
import ZichtbaarDoc from "./components/docs/ZichtbaarDoc";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { company, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!company) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin route component
const AdminRoute = ({ children }) => {
  const { company, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!company) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Feature route component
const FeatureRoute = ({ children, feature }) => {
  const { company, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!company) {
    return <Navigate to="/login" replace />;
  }

  // Check permission
  const hasPermission =
    !company.permissions ||
    company.permissions[feature] === true ||
    company.permissions[feature] === 1;

  if (!hasPermission) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MessageProvider>
          <AnalyticsRoutes />
          <Routes>
            {/* Countdown page */}
            <Route path="/" element={<Login />} />

            {/* Login page (no header/footer) */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/auth/mollie/callback" element={<MollieCallback />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Developer-documentatie (admin-only) */}
            <Route
              path="/docs"
              element={
                <AdminRoute>
                  <ZichtbaarDoc />
                </AdminRoute>
              }
            />

            {/* Dashboard routes (protected, custom layout) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            >
              <Route index element={<CompanyInfo />} />
              <Route
                path="items"
                element={
                  <FeatureRoute feature="items">
                    <ItemsManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="shop"
                element={
                  <FeatureRoute feature="shop">
                    <ShopManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="orders"
                element={
                  <FeatureRoute feature="orders">
                    <OrderManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="reservations"
                element={
                  <FeatureRoute feature="reservations">
                    <ReservationsManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="events"
                element={
                  <FeatureRoute feature="events">
                    <EventsManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="events/new"
                element={
                  <FeatureRoute feature="events">
                    <EventEditor />
                  </FeatureRoute>
                }
              />
              <Route
                path="events/:eventId"
                element={
                  <FeatureRoute feature="events">
                    <EventEditor />
                  </FeatureRoute>
                }
              />
              <Route
                path="events/:eventId/registrations"
                element={
                  <FeatureRoute feature="events">
                    <EventRegistrations />
                  </FeatureRoute>
                }
              />
              <Route
                path="popups"
                element={
                  <FeatureRoute feature="popups">
                    <PopupsManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="configurator"
                element={
                  <FeatureRoute feature="configurator">
                    <ConfiguratorManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="configurator/:configuratorId"
                element={
                  <FeatureRoute feature="configurator">
                    <ConfiguratorEditor />
                  </FeatureRoute>
                }
              />
              <Route
                path="jobs"
                element={
                  <FeatureRoute feature="jobs">
                    <JobApplicationsManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="learning-platform"
                element={
                  <FeatureRoute feature="learningPlatform">
                    <LearningPlatformManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="ai-video-analyst"
                element={
                  <FeatureRoute feature="aiVideoAnalyst">
                    <AIVideoAnalystManager />
                  </FeatureRoute>
                }
              />
              <Route path="offertes" element={<OffertesManager />} />
              <Route
                path="offertes/nieuw"
                element={<OfferteForm mode="new" />}
              />
              <Route
                path="offertes/edit/:offerteId"
                element={<OfferteForm mode="edit" />}
              />
              <Route
                path="offertes/details/:offerteId"
                element={<OfferteDetails />}
              />
              <Route
                path="offertes/settings"
                element={<OfferteSettingsForm />}
              />
              <Route
                path="tickets"
                element={
                  <FeatureRoute feature="tickets">
                    <TicketsManager />
                  </FeatureRoute>
                }
              />
              <Route
                path="statistics"
                element={
                  <FeatureRoute feature="statistics">
                    <Statistics />
                  </FeatureRoute>
                }
              />
              <Route path="settings" element={<Settings />} />
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="terms" element={<TermsOfService />} />
              <Route
                path="inbox"
                element={
                  <FeatureRoute feature="inbox">
                    <InboxManager />
                  </FeatureRoute>
                }
              />
              <Route path="companies" element={<CompaniesManager />} />
              <Route path="admin-tickets" element={<AdminTicketsManager />} />
              <Route path="admin-items" element={<AdminItemsManager />} />
              <Route path="admin-files" element={<AdminFilesManager />} />
              <Route path="admin-popups" element={<AdminPopupsManager />} />
              <Route
                path="admin-configurators"
                element={<AdminConfiguratorsManager />}
              />
              <Route
                path="admin-configurators/:companyId/:configuratorId"
                element={<ConfiguratorEditor />}
              />
              <Route
                path="admin-jobs"
                element={<AdminJobApplicationsManager />}
              />
              <Route
                path="admin-shop"
                element={<AdminShopManager />}
              />
              <Route
                path="files"
                element={
                  <FeatureRoute feature="files">
                    <FilesViewer />
                  </FeatureRoute>
                }
              />
            </Route>

            {/* Not found page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieBanner />
        </MessageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
